import {
  Alert,
  Box,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  TransferTransportDetails,
  TransferTransports,
  TransferTransportType,
} from '@bandido/shared/types';

interface Props {
  enabled: boolean;
  onEnabled: (enabled: boolean) => void;
  value: TransferTransports;
  onChange: (value: TransferTransports) => void;
  supported: TransferTransportType[];
  roundTrip: boolean;
  invalid: boolean;
  legacyRequired: boolean;
}

/** La llegada/salida del transporte es independiente de la recogida ya cotizada. */
export function TransferTransportsFields({
  enabled,
  onEnabled,
  value,
  onChange,
  supported,
  roundTrip,
  invalid,
  legacyRequired,
}: Props) {
  const { t } = useTranslation();
  if (!supported.length && !enabled) return null;
  const update = (
    direction: 'outbound' | 'return',
    end: 'origin' | 'destination',
    details?: TransferTransportDetails,
  ) => {
    const ends = { ...value[direction] };
    if (details) ends[end] = details;
    else delete ends[end];
    const next = { ...value, [direction]: ends };
    if (!Object.keys(ends).length) delete next[direction];
    onChange(next);
  };
  return (
    <Stack
      spacing={1.5}
      mt={2}
      sx={{ '@media (pointer: coarse)': { '& input, & .MuiSelect-select': { fontSize: '16px' } } }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={legacyRequired && !enabled}
            onChange={(_, checked) => onEnabled(checked)}
          />
        }
        label={t('travelServices.transportDetails.enable')}
      />
      {enabled && (
        <>
          <Typography variant="body2" color="text.secondary">
            {t('travelServices.transportDetails.help')}
          </Typography>
          {(['outbound', ...(roundTrip ? ['return'] : [])] as ('outbound' | 'return')[]).map((direction) => (
            <Box key={direction}>
              <Typography variant="subtitle2">{t(`travelServices.booking.${direction}`)}</Typography>
              {(['origin', 'destination'] as const).map((end) => {
                const entry = value[direction]?.[end];
                const prefix = `${t(`travelServices.booking.${direction}`)} · ${t(`travelServices.transportDetails.${end}`)}`;
                return (
                  <Box key={end} mt={1}>
                    <Typography variant="body2" mb={1}>
                      {t(`travelServices.transportDetails.${end}`)}
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label={`${prefix} · ${t('travelServices.transportDetails.type')}`}
                          value={entry?.type ?? ''}
                          onChange={(event) =>
                            update(
                              direction,
                              end,
                              event.target.value
                                ? { type: event.target.value as TransferTransportType, number: '' }
                                : undefined,
                            )
                          }
                        >
                          <MenuItem value="">{t('travelServices.transportDetails.none')}</MenuItem>
                          {supported.map((type) => (
                            <MenuItem key={type} value={type}>
                              {t(`travelServices.transportDetails.${type}`)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      {entry && (
                        <>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              size="small"
                              required
                              label={`${prefix} · ${t(entry.type === 'cruise' ? 'travelServices.transportDetails.ship' : 'travelServices.transportDetails.number')}`}
                              value={entry.number}
                              error={!entry.number.trim()}
                              inputProps={{ maxLength: 160 }}
                              onChange={(event) =>
                                update(direction, end, { ...entry, number: event.target.value })
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label={`${prefix} · ${t('travelServices.transportDetails.company')}`}
                              value={entry.company ?? ''}
                              inputProps={{ maxLength: 160 }}
                              onChange={(event) =>
                                update(direction, end, { ...entry, company: event.target.value || undefined })
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              size="small"
                              type="datetime-local"
                              label={`${prefix} · ${t('travelServices.transportDetails.localTime')}`}
                              InputLabelProps={{ shrink: true }}
                              value={entry.scheduledLocalTime ?? ''}
                              onChange={(event) =>
                                update(direction, end, {
                                  ...entry,
                                  scheduledLocalTime: event.target.value || undefined,
                                })
                              }
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Box>
                );
              })}
            </Box>
          ))}
          {(invalid || legacyRequired) && (
            <Alert severity="warning">{t('travelServices.transportDetails.invalid')}</Alert>
          )}
        </>
      )}
    </Stack>
  );
}
